import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCanEdit } from '@/lib/queries';
import { adminLinks } from '@/lib/links';
import { shouldSearch, useDebounced, useSearch } from '@/lib/search';
import { ErrorNote } from '@/components/Layout';

interface Helper { id: string; person: { id: string; first_name: string; last_name: string; user_id: string | null } | null }

/**
 * Members who may work this event's check-in desk without being admins. They need
 * their own login; they can check people in, take money and book walk-ins for this
 * event only, and every payment they take is recorded under their name.
 */
export function DeskHelpers({ eventId }: { eventId: string }) {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const dq = useDebounced(q, 150);
  const found = useSearch(dq, { limit: 8, perKind: 8 });
  const helpers = useQuery({
    queryKey: ['event-desk', eventId],
    queryFn: async () => {
      const { data, error } = await supabase.from('event_desk').select('id,person:person_id(id,first_name,last_name,user_id)').eq('event_id', eventId).order('added_at');
      if (error) throw error;
      return data as unknown as Helper[];
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['event-desk', eventId] });
  async function add(personId: string) {
    setError(''); setQ('');
    const { error } = await supabase.from('event_desk').insert({ event_id: eventId, person_id: personId });
    if (error) setError(/duplicate/.test(error.message) ? 'Already a desk helper.' : 'Could not add them.');
    await refresh();
  }
  async function remove(id: string) {
    await supabase.from('event_desk').delete().eq('id', id);
    await refresh();
  }
  const people = (found.data ?? []).filter(r => r.kind === 'person');

  return (
    <section className="card space-y-3">
      <div>
        <h2 className="font-semibold">Desk helpers</h2>
        <p className="text-sm text-ink-500">Members who can work this event's check-in desk without being admins: check people in, take cash or bank transfers, record pay later, and book walk-ins. Only for this event. They open it from My household.</p>
      </div>
      {helpers.data && helpers.data.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {helpers.data.map(h => h.person && (
            <li key={h.id} className="inline-flex items-center gap-1 rounded-sm border border-ink-400 py-1 pl-2.5 pr-1 text-sm">
              <Link to={adminLinks.person(h.person.id)} className="font-semibold hover:underline">{h.person.first_name} {h.person.last_name}</Link>
              {!h.person.user_id && <span className="text-xs text-brand-700">· no login yet</span>}
              {canEdit && <button type="button" className="rounded-sm p-0.5 text-ink-500 hover:bg-stone-100" aria-label={`Remove ${h.person.first_name}`} onClick={() => remove(h.id)}><X size={14} /></button>}
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-ink-500">No desk helpers. Admins can always work the desk.</p>}
      {canEdit && (
        <div className="relative max-w-md">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-9" value={q} onChange={e => setQ(e.target.value)} placeholder="Add a helper: type a member's name" aria-label="Add a desk helper" />
          {shouldSearch(q) && (
            <ul className="card absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto p-0 py-1 shadow-lg">
              {!people.length && <li className="px-4 py-2 text-sm text-ink-500">{found.isLoading ? 'Searching…' : 'No member matches.'}</li>}
              {people.map(p => <li key={p.id}><button type="button" className="block w-full px-4 py-2 text-left hover:bg-stone-100" onClick={() => add(p.id)}><span className="font-semibold">{p.title}</span> <span className="text-sm text-ink-500">{p.subtitle}</span></button></li>)}
            </ul>
          )}
        </div>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
    </section>
  );
}
